<script setup>
import { reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';

const props = defineProps({
  campaign: {
    type: Object,
    default: null,
  },
  mode: {
    type: String,
    default: 'create', // 'create' | 'edit' | 'duplicate'
  },
});

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();
const store = useStore();

const MAX_TEMPLATES = 5;

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getInboxes'),
};

onMounted(() => {
  store.dispatch('inboxes/get');
  store.dispatch('labels/get');
});

const getLocalDateTimeString = (dateObj = new Date()) => {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const currentDateTime = computed(() => getLocalDateTimeString(new Date()));

const getInitialScheduledAt = () => {
  if (props.mode === 'edit' && props.campaign?.scheduled_at) {
    const ts = props.campaign.scheduled_at < 10000000000 ? props.campaign.scheduled_at * 1000 : props.campaign.scheduled_at;
    return getLocalDateTimeString(new Date(ts));
  }
  return getLocalDateTimeString(new Date());
};

const getInitialTitle = () => {
  if (props.mode === 'duplicate' && props.campaign?.title) {
    return `[Cópia] ${props.campaign.title}`;
  }
  return props.campaign?.title || '';
};

const parseInitialTemplates = (rawMessage) => {
  if (!rawMessage || typeof rawMessage !== 'string' || !rawMessage.trim()) {
    return [''];
  }
  const parts = rawMessage
    .split(/\n\s*---\s*\n/)
    .map(t => t.trim())
    .filter(t => t.length > 0);
  return parts.length > 0 ? parts : [rawMessage.trim()];
};

const getInitialCheckpoint = () => {
  if (props.mode === 'edit') {
    return props.campaign?.trigger_rules?.checkpoint_label || '';
  }
  return '';
};

const getInitialBusinessDays = () => {
  const saved = props.campaign?.trigger_rules?.business_hours_days || props.campaign?.template_params?.business_hours_days;
  if (Array.isArray(saved) && saved.length > 0) {
    return saved.map(Number);
  }
  return [1, 2, 3, 4, 5]; // Padrão: Segunda a Sexta
};

const getInitialBusinessStart = () => {
  return props.campaign?.trigger_rules?.business_hours_start || props.campaign?.template_params?.business_hours_start || '08:00';
};

const getInitialBusinessEnd = () => {
  return props.campaign?.trigger_rules?.business_hours_end || props.campaign?.template_params?.business_hours_end || '18:00';
};

const kanbanStageOptions = computed(() => {
  const allAttributes = store.getters['attributes/getAttributesByModel']('conversation_attribute') || [];
  const listAttr = allAttributes.find(a => a.attribute_key === 'kanban_stage' || a.attribute_display_type === 'list');
  
  let stages = ['Lead', 'Proposta', 'Negociação', 'Fechado', 'Perdido'];
  if (listAttr && Array.isArray(listAttr.attribute_values) && listAttr.attribute_values.length > 0) {
    stages = listAttr.attribute_values;
  }

  return stages.map(s => ({
    value: s,
    label: s,
  }));
});

const audienceList = computed(() =>
  formState.labels.value?.map(item => ({
    value: item.id,
    label: item.title,
  })) ?? []
);

const inboxOptions = computed(() => {
  const all = formState.inboxes.value || [];
  const filtered = all.filter(item => {
    const ct = (item.channel_type || '').toLowerCase();
    return (
      ct.includes('api') ||
      ct.includes('whatsapp') ||
      ct.includes('sms') ||
      ct.includes('twilio')
    );
  });

  const list = filtered.length > 0 ? filtered : all;

  return list.map(item => {
    let typeLabel = 'Evolution API (Sem API Oficial)';
    if (item.channel_type === 'Channel::Whatsapp') typeLabel = 'WhatsApp Cloud API';
    else if (item.channel_type === 'Channel::Api') typeLabel = 'WhatsApp (Evolution API)';
    else if (item.channel_type === 'Channel::TwilioSms') typeLabel = 'Twilio SMS/WhatsApp';
    else if (item.channel_type === 'Channel::WebWidget') typeLabel = 'Live Chat';
    else if (item.channel_type === 'Channel::Email') typeLabel = 'E-mail';

    return {
      value: item.id,
      label: `${item.name} — ${typeLabel}`,
    };
  });
});

const daysList = [
  { id: 1, label: 'Seg', name: 'Segunda' },
  { id: 2, label: 'Ter', name: 'Terça' },
  { id: 3, label: 'Qua', name: 'Quarta' },
  { id: 4, label: 'Qui', name: 'Quinta' },
  { id: 5, label: 'Sex', name: 'Sexta' },
  { id: 6, label: 'Sáb', name: 'Sábado' },
  { id: 0, label: 'Dom', name: 'Domingo' },
];

const initialState = {
  title: getInitialTitle(),
  templates: parseInitialTemplates(props.campaign?.message),
  inboxId: props.campaign?.inbox?.id || props.campaign?.inbox_id || null,
  scheduledAt: getInitialScheduledAt(),
  selectedAudience: props.campaign?.audience?.map(a => a.id) || [],
  excludedAudience: props.campaign?.trigger_rules?.excluded_labels || [],
  selectedKanbanStages: props.campaign?.trigger_rules?.kanban_stages || [],
  excludedKanbanStages: props.campaign?.trigger_rules?.excluded_kanban_stages || [],
  aiRewrite: props.campaign?.trigger_rules?.ai_rewrite ?? true,
  checkpointTag: getInitialCheckpoint(),
  businessHoursOnly: props.campaign?.trigger_only_during_business_hours ?? true,
  businessHoursStart: getInitialBusinessStart(),
  businessHoursEnd: getInitialBusinessEnd(),
  businessHoursDays: getInitialBusinessDays(),
};

const state = reactive({ ...initialState });

const addTemplate = () => {
  if (state.templates.length < MAX_TEMPLATES) {
    state.templates.push('');
  }
};

const removeTemplate = (index) => {
  if (state.templates.length > 1) {
    state.templates.splice(index, 1);
  }
};

const toggleBusinessDay = (dayId) => {
  const idx = state.businessHoursDays.indexOf(dayId);
  if (idx > -1) {
    if (state.businessHoursDays.length > 1) {
      state.businessHoursDays.splice(idx, 1);
    }
  } else {
    state.businessHoursDays.push(dayId);
    state.businessHoursDays.sort((a, b) => {
      const orderA = a === 0 ? 7 : a;
      const orderB = b === 0 ? 7 : b;
      return orderA - orderB;
    });
  }
};

const setWeekdaysOnly = () => {
  state.businessHoursDays = [1, 2, 3, 4, 5];
};

const setAllDays = () => {
  state.businessHoursDays = [1, 2, 3, 4, 5, 6, 0];
};

const rules = {
  title: { required, minLength: minLength(1) },
  inboxId: { required },
  scheduledAt: { required },
};

const v$ = useVuelidate(rules, state);

const isProcessing = computed(() => {
  const flags = formState.uiFlags.value;
  return flags?.isCreating || flags?.isUpdating || false;
});

const getErrorMessage = (field, fallbackText) => {
  return v$.value[field]?.$error ? fallbackText : '';
};

const templateError = computed(() => {
  const first = state.templates[0];
  if (!first || !first.trim()) {
    return 'Por favor, preencha a mensagem do Template Principal';
  }
  return '';
});

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'Por favor, insira o título da campanha'),
  template: templateError.value,
  inbox: getErrorMessage('inboxId', 'Selecione uma caixa de entrada WhatsApp (Evolution API)'),
  scheduledAt: getErrorMessage('scheduledAt', 'Selecione a data/hora do disparo'),
}));

const isSubmitDisabled = computed(() => {
  return v$.value.$invalid || !state.templates[0] || !state.templates[0].trim();
});

const formatToUTCString = localDateTime => {
  if (!localDateTime) return null;
  return new Date(localDateTime).toISOString();
};

const handleCancel = () => emit('cancel');

const prepareCampaignDetails = () => {
  const validTemplates = state.templates
    .map(t => (typeof t === 'string' ? t.trim() : ''))
    .filter(t => t.length > 0);

  const finalMessage = validTemplates.join('\n\n---\n\n');

  return {
    title: state.title,
    message: finalMessage,
    inbox_id: state.inboxId,
    scheduled_at: formatToUTCString(state.scheduledAt),
    trigger_only_during_business_hours: state.businessHoursOnly,
    audience: state.selectedAudience?.map(id => ({
      id,
      type: 'Label',
    })),
    trigger_rules: {
      excluded_labels: state.excludedAudience || [],
      kanban_stages: state.selectedKanbanStages || [],
      excluded_kanban_stages: state.excludedKanbanStages || [],
      checkpoint_label: state.checkpointTag || '',
      ai_rewrite: state.aiRewrite,
      min_delay: 30,
      max_delay: 180,
      business_hours_enabled: state.businessHoursOnly,
      business_hours_start: state.businessHoursStart,
      business_hours_end: state.businessHoursEnd,
      business_hours_days: state.businessHoursDays
    },
    template_params: {
      ai_rewrite: state.aiRewrite,
      kanban_stages: state.selectedKanbanStages || [],
      excluded_kanban_stages: state.excludedKanbanStages || [],
      excluded_labels: state.excludedAudience || [],
      checkpoint_label: state.checkpointTag || '',
      business_hours_enabled: state.businessHoursOnly,
      business_hours_start: state.businessHoursStart,
      business_hours_end: state.businessHoursEnd,
      business_hours_days: state.businessHoursDays
    }
  };
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid || isSubmitDisabled.value) return;

  emit('submit', prepareCampaignDetails());
};
</script>

<template>
  <form class="flex flex-col gap-5" @submit.prevent="handleSubmit">
    <!-- Título da Campanha -->
    <Input
      v-model="state.title"
      label="Título da Campanha"
      placeholder="Ex: Campanha Revisão Benefício - Setembro"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <!-- Seleção da Caixa de Entrada WhatsApp / Evolution API (Sem API Oficial) -->
    <div class="flex flex-col gap-1">
      <div class="flex items-center justify-between">
        <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
          Caixa de Entrada WhatsApp (Instância Evolution / Sem API Oficial)
        </label>
        <span class="text-[11px] text-[#2B8EFF] font-medium">Evolution API</span>
      </div>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        placeholder="Selecione a instância WhatsApp que fará o disparo"
        search-placeholder="Buscar caixa de entrada..."
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <!-- SEÇÃO KANBAN: Filtro por Etapas -->
    <div class="bg-blue-50/50 dark:bg-blue-950/20 p-4 rounded-xl border border-blue-200/60 dark:border-blue-800/40 flex flex-col gap-3">
      <div class="flex items-center gap-2">
        <span class="text-[#2B8EFF]">
          <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
          </svg>
        </span>
        <span class="text-sm font-semibold text-n-slate-12">Filtragem por Etapas do Kanban</span>
      </div>

      <!-- Etapa Obrigatória (Must Have Stage) -->
      <div class="flex flex-col gap-1">
        <div class="flex items-center justify-between">
          <label class="text-xs font-medium text-n-slate-12">
            📌 Etapa Obrigatória do Kanban (Must Have)
          </label>
          <span class="text-[11px] text-n-slate-11">Vazio = Qualquer etapa</span>
        </div>
        <TagMultiSelectComboBox
          v-model="state.selectedKanbanStages"
          :options="kanbanStageOptions"
          label="Etapas Obrigatórias"
          placeholder="Selecione as etapas do Kanban (ex: Lead, Proposta)"
          class="[&>div>button]:bg-white dark:[&>div>button]:bg-n-solid-3"
        />
      </div>

      <!-- Etapa de Exclusão (Must Not Have Stage) -->
      <div class="flex flex-col gap-1">
        <div class="flex items-center justify-between">
          <label class="text-xs font-medium text-rose-600 dark:text-rose-400">
            🚫 Etapa de Exclusão do Kanban (Must Not Have)
          </label>
          <span class="text-[11px] text-n-slate-11">Ignora quem estiver nestas etapas</span>
        </div>
        <TagMultiSelectComboBox
          v-model="state.excludedKanbanStages"
          :options="kanbanStageOptions"
          label="Etapas de Exclusão"
          placeholder="Selecione as etapas a ignorar (ex: Fechado, Perdido)"
          class="[&>div>button]:bg-white dark:[&>div>button]:bg-n-solid-3 border-rose-200 dark:border-rose-900/40"
        />
      </div>
    </div>

    <!-- Filtro Booleano: Tags Obrigatórias (Must Have) -->
    <div class="flex flex-col gap-1">
      <div class="flex items-center justify-between">
        <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
          🎯 Tags Obrigatórias (Must Have)
        </label>
        <span class="text-xs text-n-slate-11">Vazio = Todos com telefone</span>
      </div>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        label="Tags Obrigatórias"
        placeholder="Selecione as etiquetas que o contato DEVE ter"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <!-- Filtro Booleano: Tags de Exclusão (Must Not Have / Blacklist) -->
    <div class="flex flex-col gap-1">
      <div class="flex items-center justify-between">
        <label for="excluded_audience" class="mb-0.5 text-sm font-medium text-n-slate-12 text-rose-600 dark:text-rose-400">
          🚫 Tags de Exclusão (Must Not Have / Blacklist)
        </label>
        <span class="text-xs text-n-slate-11">Ignora quem tiver estas tags</span>
      </div>
      <TagMultiSelectComboBox
        v-model="state.excludedAudience"
        :options="audienceList"
        label="Tags de Exclusão"
        placeholder="Ex: cliente-ativo, blacklist, opt-out"
        class="[&>div>button]:bg-n-alpha-black2 border-rose-200 dark:border-rose-900/40"
      />
    </div>

    <!-- SEÇÃO DE TEMPLATES ESPECÍFICOS PARA VARIAÇÃO -->
    <div class="flex flex-col gap-3 p-4 bg-slate-50/70 dark:bg-n-alpha-black2 rounded-xl border border-n-weak">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <span class="text-[#2B8EFF] font-bold text-sm">✉️</span>
          <label class="text-sm font-semibold text-n-slate-12">
            Templates de Mensagem (Variação A/B/C/D/E)
          </label>
        </div>
        <span class="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-[#2B8EFF]/10 text-[#2B8EFF] border border-[#2B8EFF]/20">
          {{ state.templates.length }} de {{ MAX_TEMPLATES }} variações
        </span>
      </div>

      <p class="text-xs text-n-slate-11 leading-relaxed">
        Cada contato receberá aleatoriamente uma das variações abaixo para alternar textos e prevenir bloqueios do WhatsApp.
      </p>

      <!-- Lista de Campos de Templates Individuais -->
      <div class="flex flex-col gap-3.5 mt-1">
        <div
          v-for="(tpl, index) in state.templates"
          :key="index"
          class="flex flex-col gap-1.5 p-3.5 bg-white dark:bg-n-solid-3 rounded-xl border border-n-weak/80 shadow-2xs transition-all"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-1.5">
              <span
                class="text-[11px] font-bold px-2 py-0.5 rounded-md"
                :class="index === 0 ? 'bg-[#2B8EFF] text-white' : 'bg-slate-200 dark:bg-n-solid-2 text-n-slate-12'"
              >
                {{ index === 0 ? 'Template 1 (Principal)' : `Template ${index + 1} (Variação ${String.fromCharCode(65 + index)})` }}
              </span>
              <span v-if="index === 0" class="text-[11px] text-n-slate-11 font-medium">
                *Obrigatório
              </span>
            </div>

            <!-- Botão de Remover Template (Apenas para variações adicionais) -->
            <button
              v-if="index > 0"
              type="button"
              class="text-xs text-rose-500 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-300 font-medium flex items-center gap-1 cursor-pointer transition-colors p-1"
              title="Remover este template"
              @click="removeTemplate(index)"
            >
              <svg class="size-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span>Excluir</span>
            </button>
          </div>

          <TextArea
            v-model="state.templates[index]"
            :placeholder="index === 0 ? 'Olá {{ contact.first_name }}, tudo bem? Notei que você tinha interesse em...' : 'Oi {{ contact.first_name }}! Passando para te atualizar sobre...'"
            :rows="4"
            show-character-count
            :max-length="4096"
            :message="index === 0 ? formErrors.template : ''"
            :message-type="index === 0 && formErrors.template ? 'error' : 'info'"
            class="mt-1"
          />
        </div>
      </div>

      <!-- Botão Adicionar Mais um Template -->
      <button
        v-if="state.templates.length < MAX_TEMPLATES"
        type="button"
        class="w-full py-2.5 px-4 rounded-xl border-2 border-dashed border-[#2B8EFF]/40 hover:border-[#2B8EFF] bg-[#2B8EFF]/5 hover:bg-[#2B8EFF]/10 text-[#2B8EFF] text-xs font-semibold flex items-center justify-center gap-2 cursor-pointer transition-all mt-1"
        @click="addTemplate"
      >
        <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 4v16m8-8H4" />
        </svg>
        <span>+ Adicionar Variação de Template (Template {{ state.templates.length + 1 }})</span>
      </button>

      <div v-else class="text-center py-1.5 text-xs text-n-slate-11 italic">
        ✅ Limite máximo de 5 variações de template atingido.
      </div>

      <!-- Guia de Variáveis Dinâmicas & Spintax -->
      <div v-pre class="bg-n-alpha-2 p-3 rounded-lg border border-n-weak text-xs text-n-slate-11 flex flex-col gap-1 mt-1">
        <p>💡 <b>Variáveis Dinâmicas:</b> <code class="text-[#2B8EFF] font-mono">&#123;&#123; contact.first_name &#125;&#125;</code>, <code class="text-[#2B8EFF] font-mono">&#123;&#123; contact.name &#125;&#125;</code>, <code class="text-[#2B8EFF] font-mono">&#123;&#123; contact.phone_number &#125;&#125;</code>.</p>
        <p>🔀 <b>Spintax Suportado:</b> <code class="text-amber-600 dark:text-amber-400 font-mono">&#123;Olá|Oi|Como vai&#125;</code> sorteia sinônimos dentro de cada template.</p>
        <p>📏 <b>Limite Expandido:</b> Até <b>4.096 caracteres</b> por template.</p>
      </div>
    </div>

    <!-- SEÇÃO HORÁRIO COMERCIAL E CADÊNCIA ANTI-BLOQUEIO -->
    <div class="bg-[#2B8EFF]/5 p-4 rounded-xl border border-[#2B8EFF]/30 flex flex-col gap-3.5">
      <!-- Variação com IA -->
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <span class="text-[#2B8EFF]">
            <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </span>
          <span class="text-sm font-medium text-n-slate-12">Variação Humanizada com IA (OpenAI)</span>
        </div>
        <input
          type="checkbox"
          v-model="state.aiRewrite"
          class="h-4 w-4 rounded border-slate-300 text-[#2B8EFF] focus:ring-[#2B8EFF] cursor-pointer"
        />
      </div>
      <p class="text-xs text-n-slate-11">
        Reescreve cada mensagem de forma única para cada cliente mantendo a oferta e o tom profissional, prevenindo bloqueios do WhatsApp.
      </p>

      <!-- Badge de Cadência Humana Anti-Bloqueio -->
      <div class="p-3 bg-white/80 dark:bg-n-solid-3 rounded-lg border border-[#2B8EFF]/30 text-xs text-n-slate-11 flex flex-col gap-1 shadow-2xs">
        <div class="flex items-center gap-1.5 font-bold text-n-slate-12">
          <span class="text-[#2B8EFF]">⚡</span>
          <span>Cadência Humana Anti-Bloqueio Ativa:</span>
        </div>
        <p class="leading-relaxed text-[11px]">
          Delay randômico de <b>30s a 180s</b> entre disparos + <b>Pausa programada de 3 minutos (180s)</b> a cada 5 mensagens enviadas para simular comportamento e digitação humana real.
        </p>
      </div>

      <hr class="border-t border-[#2B8EFF]/20" />

      <!-- CONFIGURAÇÃO DE HORÁRIO COMERCIAL (EDITÁVEL) -->
      <div class="flex flex-col gap-3">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-1.5 font-semibold text-xs text-n-slate-12">
            <span>⏰</span>
            <span>Restrição de Horário de Disparo (Horário Comercial)</span>
          </div>
          <input
            type="checkbox"
            v-model="state.businessHoursOnly"
            class="h-4 w-4 rounded border-slate-300 text-[#2B8EFF] focus:ring-[#2B8EFF] cursor-pointer"
          />
        </div>

        <!-- Bloco expansível de Horário Comercial -->
        <div
          v-if="state.businessHoursOnly"
          class="p-3.5 bg-white/90 dark:bg-n-solid-3 rounded-xl border border-n-weak flex flex-col gap-3 shadow-2xs"
        >
          <!-- Seletor de Horários Início / Fim -->
          <div class="grid grid-cols-2 gap-3">
            <div class="flex flex-col gap-1">
              <label class="text-[11px] font-medium text-n-slate-11">Horário Inicial (Abertura)</label>
              <input
                type="time"
                v-model="state.businessHoursStart"
                class="w-full text-xs font-semibold px-2.5 py-1.5 bg-white dark:bg-n-solid-2 border border-n-weak rounded-lg outline-none focus:ring-1 focus:ring-[#2B8EFF] text-n-slate-12"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-[11px] font-medium text-n-slate-11">Horário Limite (Fechamento)</label>
              <input
                type="time"
                v-model="state.businessHoursEnd"
                class="w-full text-xs font-semibold px-2.5 py-1.5 bg-white dark:bg-n-solid-2 border border-n-weak rounded-lg outline-none focus:ring-1 focus:ring-[#2B8EFF] text-n-slate-12"
              />
            </div>
          </div>

          <!-- Seletor de Dias da Semana -->
          <div class="flex flex-col gap-1.5">
            <div class="flex items-center justify-between">
              <label class="text-[11px] font-medium text-n-slate-11">Dias Permitidos para Disparo:</label>
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="text-[10px] text-[#2B8EFF] hover:underline font-medium cursor-pointer"
                  @click="setWeekdaysOnly"
                >
                  Seg-Sex
                </button>
                <span class="text-n-weak text-[10px]">|</span>
                <button
                  type="button"
                  class="text-[10px] text-[#2B8EFF] hover:underline font-medium cursor-pointer"
                  @click="setAllDays"
                >
                  Todos
                </button>
              </div>
            </div>

            <!-- Botões dos Dias -->
            <div class="grid grid-cols-7 gap-1.5">
              <button
                v-for="day in daysList"
                :key="day.id"
                type="button"
                :title="day.name"
                class="py-1.5 text-center text-xs font-bold rounded-lg border transition-all cursor-pointer"
                :class="state.businessHoursDays.includes(day.id)
                  ? 'bg-[#2B8EFF] text-white border-[#2B8EFF] shadow-2xs'
                  : 'bg-slate-100 dark:bg-n-solid-2 text-n-slate-11 border-transparent hover:bg-slate-200 dark:hover:bg-n-solid-1'"
                @click="toggleBusinessDay(day.id)"
              >
                {{ day.label }}
              </button>
            </div>
          </div>

          <p class="text-[11px] text-n-slate-11 leading-relaxed italic bg-n-alpha-1 p-2 rounded-lg">
            ℹ️ O robô só enviará mensagens entre as <b>{{ state.businessHoursStart }}</b> e <b>{{ state.businessHoursEnd }}</b> nos dias selecionados. Fora desse período, a campanha aguardará automaticamente a próxima janela útil.
          </p>
        </div>
      </div>

      <hr class="border-t border-[#2B8EFF]/20" />

      <!-- Etiqueta Pós-Disparo (Checkpoint) -->
      <div class="flex flex-col gap-1">
        <div class="flex items-center justify-between">
          <label class="text-xs font-medium text-n-slate-12">
            🏷️ Etiqueta de Controle Pós-Disparo (Checkpoint Anti-Duplicação)
          </label>
          <span class="text-[10px] text-n-slate-11">Deixe vazio para gerar tag única automática</span>
        </div>
        <input
          type="text"
          v-model="state.checkpointTag"
          placeholder="Deixe vazio (Gera automaticamente cp-enviada-[id])"
          class="w-full text-xs px-3 py-2 bg-white dark:bg-n-solid-3 border border-n-weak rounded-lg outline-none focus:ring-1 focus:ring-[#2B8EFF]"
        />
      </div>
    </div>

    <!-- Data e Hora do Disparo -->
    <Input
      v-model="state.scheduledAt"
      label="Data e Hora do Disparo"
      type="datetime-local"
      :min="currentDateTime"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <!-- Botões -->
    <div class="flex gap-3 justify-between items-center w-full pt-2">
      <Button
        variant="faded"
        color="slate"
        type="button"
        label="Cancelar"
        class="w-full bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="mode === 'edit' ? 'Salvar Alterações' : mode === 'duplicate' ? 'Salvar Cópia da Campanha' : 'Iniciar Campanha WhatsApp'"
        class="w-full bg-[#2B8EFF] hover:bg-[#1E75D8] text-white"
        type="submit"
        :is-loading="isProcessing"
        :disabled="isProcessing || isSubmitDisabled"
      />
    </div>
  </form>
</template>